/* eslint no-underscore-dangle: ["error", { "allow": ["_getNumericId"] }] */
import { defineStore } from 'pinia';
import { sortObjectArray } from '@utils/sort-by-object-property';
import { COMMUNITY_PROGRAMME } from '@constants';

const useAppProgrammeStore = defineStore('appProgrammeStore', {
  state: () => ({
    userProgrammes: [],
    currentProgrammeId: null,
  }),
  getters: {
    programmes(state) {
      return state.userProgrammes;
    },
    currentProgramme(state) {
      // Convert to number to handle string IDs from route params
      // NOTE: We can't call actions in getters, so we need to convert to number here
      const numericId = Number(state.currentProgrammeId);
      return state.userProgrammes.find(programme => programme.id === numericId);
    },
    defaultProgrammeId(state) {
      if (!state.userProgrammes?.length) {
        return null;
      }
      // check if the user has any saved currentProgrammeId
      const savedProgrammeId = localStorage.getItem('currentProgrammeId');
      if (savedProgrammeId !== null && savedProgrammeId !== undefined && savedProgrammeId !== '') {
        return Number(savedProgrammeId);
      }
      const nonCommunityProgrammes = state.userProgrammes.filter(p => p.name !== COMMUNITY_PROGRAMME);
      if (nonCommunityProgrammes.length === 0) {
        // If user is ONLY in the community programme, return that
        return state.userProgrammes.find(p => p.name === COMMUNITY_PROGRAMME).id;
      }
      // Otherwise sort by start date and return the most recent
      return sortObjectArray(nonCommunityProgrammes, 'start_date', true)[0].id;
    },
  },
  actions: {
    _getNumericId(programmeId) {
      // Necessary because we often pass the programmeId in directly from route params, where it is a string.
      // Common 'falsy' cases:
      // - undefined returns NaN (which is also falsy)
      // - '' returns 0
      // - null returns 0
      return Number(programmeId);
    },
    clear() {
      this.userProgrammes = [];
      this.currentProgrammeId = null;
      localStorage.removeItem('currentProgrammeId');
    },
    getProgramme(programmeId) {
      const numericProgrammeId = this._getNumericId(programmeId);
      return this.userProgrammes.find(programme => programme.id === numericProgrammeId);
    },
    isProgrammeAccessible(programmeId) {
      const numericProgrammeId = this._getNumericId(programmeId);
      if (numericProgrammeId) {
        return this.userProgrammes.some(programme => programme.id === numericProgrammeId);
      }
      return false;
    },
    fetchProgrammes() {
      this.$logger.info('Getting available programmes for the learner');
      return this.$http.get('/api/curriculum/programmes')
        .then(res => {
          this.userProgrammes = res.data.programmes;
          this.currentProgrammeId = this.defaultProgrammeId;
          this.$logger.info('Got available programmes');
          return res.data.programmes; // Make sure to return data to resolve the promise properly
        })
        .catch(err => {
          this.$logger.error('Could not fetch programmes data for the user', undefined, err);
          return Promise.reject(err);
        });
    },
    initCurrentProgrammeIdWatcher() {
      this.$subscribe((mutation, state) => {
        // Only save if current store and there is valid programmeId not null or undefined
        if (mutation.storeId === this.$id && state.currentProgrammeId) {
          localStorage.setItem('currentProgrammeId', state.currentProgrammeId);
        }
      }, { flush: 'sync' });
    },
  },
});

export default useAppProgrammeStore;
